import { foldApostrophes } from "./scenario";

/**
 * Decision list item 102(a), Kabir's 102a, ruled 2026-10-01 (TA-498). The
 * fixed contact ask and the helpers that add it, skip it and read it, moved
 * here from packages/agents with no wording or behaviour change, because
 * `pnpm drafts approve --with-contact-ask` (packages/db) adds the same line at
 * approval in grace and packages/db cannot import packages/agents. The agents
 * package re-exports these, so compose and approval read one line and one
 * skip. `contactAskSkip` takes memory as anything with `content`, which is
 * all it ever read of an agents `MemoryEntry`.
 *
 * The detector `hasContactAsk` stays in packages/agents: since item 99(b) it
 * decides nothing, and nothing outside that package needs it.
 */

/**
 * Decision list item 84(a), in grace only. A no-answer turn escalates, and in
 * F-01's grace stretch it is the last in-window act, which F-02 says goes to
 * contact capture. The fixed-line counterpart of CONTACT_CAPTURE_RULE: a
 * number or email, and no promise of a text, call or email (F-20).
 *
 * Written by the implementer. Ratified by Kabir on 2026-09-29: decision list
 * item 84's rec put this line in front of him to read, and he typed "recs",
 * which took 84(a).
 */
export const NO_ANSWER_CONTACT_ASK = "What's the best number or email to reach you on?";

/**
 * Decision list item 97(a), Kabir's 97a, ruled 2026-10-01. The stored body with
 * code's appended contact ask set aside: the body before the trailing space
 * and fixed ask that `appendContactAsk` (below) adds, and the fixed holding
 * line in grace ends with too. Null when the body does not end with that ask,
 * so there is nothing to set aside.
 *
 * A period `appendContactAsk` put before the ask stays: from the stored body
 * alone it cannot be told from one the model wrote, and keeping it is the
 * strict side.
 */
export function withoutAppendedContactAsk(body: string): string | null {
  const trimmed = body.trimEnd();
  const suffix = ` ${NO_ANSWER_CONTACT_ASK}`;
  if (!trimmed.endsWith(suffix)) return null;
  return trimmed.slice(0, trimmed.length - suffix.length).trimEnd();
}

/**
 * The shapes a customer gives a contact in: the email and phone shapes
 * `redactFreeText` removes, the markers it leaves in their place, and ten or
 * eleven digits typed as one run. Not `survivingContactDetails`, which reads
 * any "@" and any date as a contact by design, and here a false hit loses the
 * ask. "[REDACTED_NUMBER]" is not read: it is as often an order number.
 */
/** TA-479: an order, reference or tracking number as long as a phone number is not a contact. */
const REFERENCE_NUMBER = /\b(?:order|ref|reference|confirmation|tracking|invoice|receipt|ticket|case)\b(?:\s*(?:number|no\.?|num|#))?\s*(?:is|was|:)?\s*#?\s*\d+(?:-\d+)*/gi;

const CONTACT_GIVEN: readonly RegExp[] = [
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  /(?:\+\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/,
  /(?<!\d)\d{10,11}(?!\d)/,
  /\[REDACTED_(?:EMAIL|PHONE)\]/,
];

/** A customer who said no to giving one, or memory that recorded it. A question is not a decline. */
const CONTACT_NOUN = String.raw`(?:number|phone|e-?mail|cell|contact)`;
const DECLINED: readonly RegExp[] = [
  new RegExp(String.raw`\b(?:i'?d|i would) (?:rather|prefer) not\b[^.!?\n]{0,30}\b${CONTACT_NOUN}\b`, "i"),
  new RegExp(String.raw`\bi (?:don'?t|do not) want to (?:give|share|send|leave)\b[^.!?\n]{0,20}\b${CONTACT_NOUN}\b`, "i"),
  new RegExp(String.raw`\b(?:i'?m|im|i am) not (?:giving|sharing|comfortable)\b[^.!?\n]{0,20}\b${CONTACT_NOUN}\b`, "i"),
  /\bi (?:don'?t|do not) have (?:a |an )?(?:e-?mail|phone|cell)\b/i,
  // TA-479: "no calls" rules out a call, not the email the ask also offers.
  /\bno (?:number|phone|e-?mail),?(?: please| pls| thanks| thank you)?\s*(?:[.!]|$)/im,
  new RegExp(String.raw`\bdeclined\b[^.!?\n]{0,30}\b${CONTACT_NOUN}\b`, "i"),
  // TA-479, TA-483: keeping it in the DMs is a decline with no contact word
  // in it. "just dm me" must end the clause: "can you just dm me here the
  // price?" asks to be told here and declines nothing.
  /\bjust (?:dm|message|msg|text) me(?: (?:here|on here|on (?:ig|insta|instagram)))?\s*(?:[,.!]|$)/im,
  /\b(?:dms?|instagram|insta|ig)(?: is| are|'s)? (?:fine|good|easier|better|best)\b/i,
  /\b(?:prefer|rather) (?:to )?(?:keep it|stay|chat|talk) (?:on|in) (?:here|(?:the )?dms?|instagram|insta|ig)\b/i,
  /\bkeep(?:ing)? it (?:on|in) (?:here|(?:the )?dms?|instagram|insta|ig)\b/i,
];

export type ContactAskSkip = "contact_known" | "declined";

/**
 * CONTACT_CAPTURE_RULE's own skip, in code: "Skip the ask if they already
 * shared a number or email, or declined." Reads the customer's turns (the
 * thread window and the message being answered) and contact memory, which is
 * data here and never an instruction (section 6).
 */
export function contactAskSkip(
  customerTurns: readonly string[],
  memory: readonly { readonly content: string }[],
): ContactAskSkip | null {
  const texts = [...customerTurns, ...memory.map((e) => e.content)].map(foldApostrophes);
  if (texts.some((t) => CONTACT_GIVEN.some((p) => p.test(t.replace(REFERENCE_NUMBER, " "))))) return "contact_known";
  if (texts.some((t) => DECLINED.some((p) => p.test(t)))) return "declined";
  return null;
}

/**
 * TA-487: the fixed ask spelled another way is still the fixed ask ("e-mail",
 * "u", "number/email", "what is", a prime for the apostrophe), or it is added
 * a second time. Only spellings of the same words: a different ask is not the
 * fixed line, and the fixed line is still added after it (item 99b).
 */
const plainWords = (s: string): string =>
  foldApostrophes(s)
    .toLowerCase()
    .replace(/['\u2032`]/g, "")
    .replace(/\//g, " or ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\be mail\b/g, "email")
    .replace(/\bu\b/g, "you")
    .replace(/\bwhat is\b/g, "whats")
    .trim();
const FIXED_ASK_WORDS = plainWords(NO_ANSWER_CONTACT_ASK);

/** Item 99(b): the reply already carries the fixed ask, ignoring case, punctuation and apostrophes, so it is not added twice. */
export function carriesFixedAsk(reply: string): boolean {
  return ` ${plainWords(reply)} `.includes(` ${FIXED_ASK_WORDS} `);
}

/** One word of `plainWords`'s output, with the span of the source it came from. */
interface PlainToken {
  readonly word: string;
  readonly start: number;
  readonly end: number;
}

/**
 * `plainWords` again, keeping where each word came from. The same steps in the
 * same order: fold, lowercase, drop apostrophes inside a word, "/" reads as
 * "or", anything else outside [a-z0-9] splits, then "e mail" -> "email",
 * "u" -> "you", "what is" -> "whats". `fixedAskSpan` matches on this, so it
 * accepts exactly the spellings `carriesFixedAsk` accepts (its test holds the
 * two to agreement).
 */
function plainTokens(s: string): PlainToken[] {
  const folded = foldApostrophes(s);
  const raw: PlainToken[] = [];
  let word = "";
  let start = -1;
  let end = -1;
  const flush = (): void => {
    if (word.length > 0) raw.push({ word, start, end });
    word = "";
    start = -1;
  };
  for (let i = 0; i < folded.length; i++) {
    const ch = folded[i] as string;
    if (/['′`]/.test(ch)) continue;
    if (ch === "/") {
      flush();
      raw.push({ word: "or", start: i, end: i + 1 });
      continue;
    }
    const low = ch.toLowerCase();
    if (/^[a-z0-9]+$/.test(low)) {
      if (start < 0) start = i;
      word += low;
      end = i + 1;
    } else {
      flush();
    }
  }
  flush();
  const merge = (ts: PlainToken[], a: string, b: string, into: string): PlainToken[] => {
    const out: PlainToken[] = [];
    for (let i = 0; i < ts.length; i++) {
      const t = ts[i] as PlainToken;
      const n = ts[i + 1];
      if (t.word === a && n?.word === b) {
        out.push({ word: into, start: t.start, end: n.end });
        i++;
      } else out.push(t);
    }
    return out;
  };
  const merged = merge(raw, "e", "mail", "email").map((t) => (t.word === "u" ? { ...t, word: "you" } : t));
  return merge(merged, "what", "is", "whats");
}

const FIXED_ASK_TOKENS: readonly string[] = FIXED_ASK_WORDS.split(" ");

/**
 * R7b fix 2, N1: where the fixed ask sits in a reply, in any spelling
 * `carriesFixedAsk` accepts, or null when it accepts none. The span runs from
 * the ask's first word to its last, plus the sentence punctuation right after
 * it ("?", "??", "?!"), so a reader that cuts it leaves no stray question mark
 * to count. The Voice gate cuts this span before measuring (the ask is a
 * registered fixed line, not the model's voice).
 */
export function fixedAskSpan(reply: string): { readonly start: number; readonly end: number } | null {
  const ts = plainTokens(reply);
  const n = FIXED_ASK_TOKENS.length;
  for (let i = 0; i + n <= ts.length; i++) {
    if (FIXED_ASK_TOKENS.every((w, k) => ts[i + k]?.word === w)) {
      let end = (ts[i + n - 1] as PlainToken).end;
      while (end < reply.length && /[?!.]/.test(reply[end] as string)) end++;
      return { start: (ts[i] as PlainToken).start, end };
    }
  }
  return null;
}

/** The fixed line's ask after a composed reply, as its own sentence. */
export function appendContactAsk(reply: string): string {
  const body = reply.trimEnd();
  // TA-480: after an emoji the ask follows a space; a period after a ring emoji read oddly.
  const ended = /[.!?\p{Extended_Pictographic}\p{Emoji_Modifier}\p{M}]$/u.test(body);
  return `${body}${ended ? "" : "."} ${NO_ANSWER_CONTACT_ASK}`;
}
